// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Types
import { Theme } from '../../../../../../types/theme';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Plugins
import {
  booleanStatusToTagType,
  controlModeToTagType,
  controlModeToText,
  enabledDisabledToText,
  runningStatusToTagType,
  runningStatusToText,
} from '../../../../../../plugins/crusher';
import { DASHBOARD_NULLISH_TEXT } from '../../../../../../plugins/general';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import { Tag } from '../../../../../../layout/elements/Tag';

// View
import { MotorControlProps } from './MotorControl.types';
import {
  BinFeederContainer,
  ControlModeContainer,
  CrusherMotor1Container,
  CrusherMotor2Container,
  DischargeConveyorContainer,
  FeederContainer,
  FeederControlModeContainer,
  HorimeterContainer,
  HorimetersStatusContent,
  HorimeterTitleContainer,
  HoursContainer,
  LubeTankHeaterContainer,
  LubeUnitContainer,
  MotorControlContainer,
} from './MotorControl.style';

function MotorControl({
  unitData,
  edgeStatus,
  testId,
  className,
}: MotorControlProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();
  const { motorControlDrawer } = theme.color.crusherDashboard;

  return (
    <MotorControlContainer
      data-testid={testId}
      className={className}
    >
      <ControlModeContainer>
        <Row>
          <Col xs={24} xl={12}>
            <FeederControlModeContainer data-testid={`${testId}-feeder-control-mode-container`}>
              <Paragraph
                weight={ParagraphWeight.bold}
                size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
              >
                {t('view.crusher.vsi.drawers.motorControl.title')}
              </Paragraph>
              <Tag
                text={controlModeToText(unitData?.motorControl)}
                type={controlModeToTagType(
                  unitData?.motorControl,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
                className="status"
              />
            </FeederControlModeContainer>
          </Col>

          <Col xs={24} xl={12}>
            <LubeTankHeaterContainer data-testid={`${testId}-lube-tank-heater-container`}>
              <Paragraph
                weight={ParagraphWeight.bold}
                size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
              >
                {t('view.crusher.vsi.drawers.motorControl.heaterEnable')}
              </Paragraph>
              <Tag
                text={enabledDisabledToText(unitData?.lubeTankHeaterEnable)}
                type={booleanStatusToTagType(
                  unitData?.lubeTankHeaterEnable,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
                className="status"
              />
            </LubeTankHeaterContainer>
          </Col>
        </Row>
      </ControlModeContainer>

      <HorimeterContainer>
        <Row>
          <Col>
            <HorimeterTitleContainer data-testid={`${testId}-horimeter-title-container`}>
              <Paragraph
                weight={ParagraphWeight.bold}
                size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
              >
                {t('view.crusher.vsi.drawers.motorControl.horimeter')}
              </Paragraph>
            </HorimeterTitleContainer>
          </Col>

          <Col xs={24} xl={unitData?.dischargeSuperiorEnable ? 12 : 24}>
            <LubeUnitContainer data-testid={`${testId}-lube-unit-container`}>
              <HorimetersStatusContent>
                <Paragraph
                  weight={ParagraphWeight.medium}
                  size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  color={motorControlDrawer.horimeterStatusTextColor}
                >
                  {t('view.crusher.vsi.drawers.motorControl.lubricationStatus')}
                </Paragraph>
                <Tag
                  text={runningStatusToText(unitData?.lubePumpRunningStatus)}
                  type={runningStatusToTagType(
                    unitData?.lubePumpRunningStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )}
                  className="status"
                />
              </HorimetersStatusContent>
              <HoursContainer>
                <Paragraph
                  weight={ParagraphWeight.bold}
                  size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
                >
                  {unitData?.lubePump1RunHours || DASHBOARD_NULLISH_TEXT}
                </Paragraph>
                <Paragraph
                  weight={ParagraphWeight.bold}
                  size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                >
                  {t('view.crusher.vsi.drawers.motorControl.hours')}
                </Paragraph>
              </HoursContainer>
            </LubeUnitContainer>
          </Col>

          {unitData?.dischargeSuperiorEnable && (
            <Col xs={24} xl={12}>
              <DischargeConveyorContainer data-testid="discharge-conveyor-container">
                <HorimetersStatusContent>
                  <Paragraph
                    weight={ParagraphWeight.medium}
                    size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                    color={motorControlDrawer.horimeterStatusTextColor}
                  >
                    {t('view.crusher.vsi.drawers.motorControl.dischargeStatus')}
                  </Paragraph>
                  <Tag
                    text={runningStatusToText(unitData?.dischargeRunningStatus)}
                    type={runningStatusToTagType(
                      unitData?.dischargeRunningStatus,
                      edgeStatus ? edgeStatus.edgeStatus : null,
                      unitData?.timestamp,
                    )}
                    className="status"
                  />
                </HorimetersStatusContent>
                <HoursContainer>
                  <Paragraph
                    weight={ParagraphWeight.bold}
                    size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
                  >
                    {unitData?.dischargeRunHours || DASHBOARD_NULLISH_TEXT}
                  </Paragraph>
                  <Paragraph
                    weight={ParagraphWeight.bold}
                    size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  >
                    {t('view.crusher.vsi.drawers.motorControl.hours')}
                  </Paragraph>
                </HoursContainer>
              </DischargeConveyorContainer>
            </Col>
          )}

          <Col xs={24} xl={unitData?.crusherMotorQty ? 12 : 24}>
            <CrusherMotor1Container data-testid={`${testId}-crusher-motor-1-container`}>
              <HorimetersStatusContent>
                <Paragraph
                  weight={ParagraphWeight.medium}
                  size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  color={motorControlDrawer.horimeterStatusTextColor}
                >
                  {t('view.crusher.vsi.drawers.motorControl.crusherMotor1Status')}
                </Paragraph>
                <Tag
                  text={runningStatusToText(unitData?.crusherMotorStatus)}
                  type={runningStatusToTagType(
                    unitData?.crusherMotorStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )}
                  className="status"
                />
              </HorimetersStatusContent>
              <HoursContainer>
                <Paragraph
                  weight={ParagraphWeight.bold}
                  size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
                >
                  {unitData?.crusherMotor1RunHours || DASHBOARD_NULLISH_TEXT}
                </Paragraph>
                <Paragraph
                  weight={ParagraphWeight.bold}
                  size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                >
                  {t('view.crusher.vsi.drawers.motorControl.hours')}
                </Paragraph>
              </HoursContainer>
            </CrusherMotor1Container>
          </Col>

          {unitData?.crusherMotorQty && (
            <Col xs={24} xl={12}>
              <CrusherMotor2Container data-testid="crusher-motor-2-container">
                <HorimetersStatusContent>
                  <Paragraph
                    weight={ParagraphWeight.medium}
                    size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                    color={motorControlDrawer.horimeterStatusTextColor}
                  >
                    {t('view.crusher.vsi.drawers.motorControl.crusherMotor2Status')}
                  </Paragraph>
                  <Tag
                    text={runningStatusToText(unitData?.crusherMotorStatus)}
                    type={runningStatusToTagType(
                      unitData?.crusherMotorStatus,
                      edgeStatus ? edgeStatus.edgeStatus : null,
                      unitData?.timestamp,
                    )}
                    className="status"
                  />
                </HorimetersStatusContent>
                <HoursContainer>
                  <Paragraph
                    weight={ParagraphWeight.bold}
                    size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
                  >
                    {unitData?.crusherMotor2RunHours || DASHBOARD_NULLISH_TEXT}
                  </Paragraph>
                  <Paragraph
                    weight={ParagraphWeight.bold}
                    size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  >
                    {t('view.crusher.vsi.drawers.motorControl.hours')}
                  </Paragraph>
                </HoursContainer>
              </CrusherMotor2Container>
            </Col>
          )}

          {unitData?.feedSuperiorEnable && (
            <Col xs={24} xl={12}>
              <FeederContainer data-testid="feeder-container">
                <HorimetersStatusContent>
                  <Paragraph
                    weight={ParagraphWeight.medium}
                    size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                    color={motorControlDrawer.horimeterStatusTextColor}
                  >
                    {t('view.crusher.vsi.drawers.motorControl.feederStatus')}
                  </Paragraph>
                  <Tag
                    text={runningStatusToText(unitData?.feederRunningStatus)}
                    type={runningStatusToTagType(
                      unitData?.feederRunningStatus,
                      edgeStatus ? edgeStatus.edgeStatus : null,
                      unitData?.timestamp,
                    )}
                    className="status"
                  />
                </HorimetersStatusContent>
                <HoursContainer>
                  <Paragraph
                    weight={ParagraphWeight.bold}
                    size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
                  >
                    {unitData?.feederRunHours || DASHBOARD_NULLISH_TEXT}
                  </Paragraph>
                  <Paragraph
                    weight={ParagraphWeight.bold}
                    size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  >
                    {t('view.crusher.vsi.drawers.motorControl.hours')}
                  </Paragraph>
                </HoursContainer>
              </FeederContainer>
            </Col>
          )}

          {unitData?.feedSuperiorEnable && (
            <Col xs={24} xl={12}>
              <BinFeederContainer data-testid="bin-feeder-container">
                <HorimetersStatusContent>
                  <Paragraph
                    weight={ParagraphWeight.medium}
                    size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                    color={motorControlDrawer.horimeterStatusTextColor}
                  >
                    {t('view.crusher.vsi.drawers.motorControl.binFeederStatus')}
                  </Paragraph>
                  <Tag
                    text={runningStatusToText(unitData?.binFeederRunningStatus)}
                    type={runningStatusToTagType(
                      unitData?.binFeederRunningStatus,
                      edgeStatus ? edgeStatus.edgeStatus : null,
                      unitData?.timestamp,
                    )}
                    className="status"
                  />
                </HorimetersStatusContent>
                <HoursContainer>
                  <Paragraph
                    weight={ParagraphWeight.bold}
                    size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
                  >
                    {unitData?.binFeederRunHours || DASHBOARD_NULLISH_TEXT}
                  </Paragraph>
                  <Paragraph
                    weight={ParagraphWeight.bold}
                    size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  >
                    {t('view.crusher.vsi.drawers.motorControl.hours')}
                  </Paragraph>
                </HoursContainer>
              </BinFeederContainer>
            </Col>
          )}
        </Row>
      </HorimeterContainer>
    </MotorControlContainer>
  );
}

export { MotorControl };
